import React, { useEffect, useRef, useState } from 'react';
import YoutubeThumbnail from './Video/YoutubeThumbnail';

// Fix video playing issues by managing global player state

// Global player registry (shared across all instances)
if (!window.activeYoutubePlayers) {
  window.activeYoutubePlayers = [];
}

function YoutubeIframe({
  videoId,
  iframeData,
  onStateChange,
  className = '',
  playWithID = false,
}) {
  const playerRef = useRef(null);
  const [isClicked, setIsClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Extract video ID from iframe data if not provided directly
  const extractVideoId = (htmlString) => {
    if (videoId) return videoId;
    if (!htmlString) return null;

    const match = htmlString.match(/(?:embed\/|v=)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  const currentVideoId = extractVideoId(iframeData);

  // Add autoplay to iframe data for seamless playback
  const getAutoplayIframeData = (originalData) => {
    if (!originalData) return originalData;

    return (
      originalData
        // Add autoplay=1 to the iframe src if not already present
        .replace(/src="([^"]*?)"/, (match, url) => {
          const separator = url.includes('?') ? '&' : '?';
          const autoplayParam = url.includes('autoplay=')
            ? ''
            : `${separator}autoplay=1`;
          return `src="${url}${autoplayParam}"`;
        })
        // Remove hardcoded width and height attributes
        .replace(/\s*width\s*=\s*["'][^"']*["']/gi, '')
        .replace(/\s*height\s*=\s*["'][^"']*["']/gi, '')
        // Add responsive styling
        .replace(
          /<iframe/,
          '<iframe style="width: 100%; height: 100%; object-fit: cover;"',
        )
    );
  };

  // Handle facade click to load real YouTube player
  const handleFacadeClick = () => {
    if (isClicked) return;

    setIsClicked(true);
    setIsLoading(true);

    // Load YouTube iframe API if not already loaded
    if (!window.YT) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      document.body.appendChild(script);
    }
  };

  // Initialize player when API is ready
  const initializePlayer = () => {
    if (playWithID && currentVideoId) {
      playerRef.current = new window.YT.Player(
        `youtube-player-${currentVideoId}`,
        {
          videoId: currentVideoId,
          playerVars: {
            autoplay: 1,
            modestbranding: 1,
            rel: 0,
            showinfo: 0,
          },
          events: {
            onReady: (event) => {
              // Add to global registry
              window.activeYoutubePlayers.push(playerRef.current);
              setIsLoading(false);
            },
            onStateChange: (event) => {
              if (event.data === window.YT.PlayerState.PLAYING) {
                // Pause other active players
                window.activeYoutubePlayers.forEach((p) => {
                  if (p !== playerRef.current && p.pauseVideo) {
                    p.pauseVideo();
                  }
                });
              }

              // Call custom handler if passed
              if (onStateChange) {
                onStateChange(event);
              }
            },
          },
        },
      );
    } else {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  // const initializePlayer = () => {
  //   if (playWithID && currentVideoId) {
  //     playerRef.current = new window.YT.Player(
  //       `youtube-player-${currentVideoId}`,
  //       {
  //         videoId: currentVideoId,
  //         playerVars: {
  //           autoplay: 1, // Auto-play when user clicks facade
  //           modestbranding: 1,
  //           rel: 0,
  //           showinfo: 0,
  //         },
  //         events: {
  //           onStateChange,
  //           onReady: () => setIsLoading(false),
  //         },
  //       },
  //     );
  //   } else {
  //     // For iframeData mode, replace with actual iframe with autoplay
  //     setTimeout(() => setIsLoading(false), 500); // Small delay for better UX
  //   }
  // };

  useEffect(() => {
    if (!isClicked || !currentVideoId || !playWithID) return;

    const loadPlayer = () => {
      if (window.YT && window.YT.Player) {
        initializePlayer();
      } else {
        window.onYouTubeIframeAPIReady = initializePlayer;

        // Load script if not already added
        if (!document.getElementById('youtube-api')) {
          const script = document.createElement('script');
          script.id = 'youtube-api';
          script.src = 'https://www.youtube.com/iframe_api';
          script.async = true;
          document.body.appendChild(script);
        }
      }
    };

    loadPlayer();
  }, [isClicked, currentVideoId, playWithID]); // only run when clicked

  // if (window.YT && window.YT.Player) {
  //   initializePlayer();
  // } else {
  //   window.onYouTubeIframeAPIReady = initializePlayer;
  // }

  useEffect(
    () => () => {
      if (playerRef.current) {
        window.activeYoutubePlayers = window.activeYoutubePlayers.filter(
          (p) => p !== playerRef.current,
        );
        playerRef.current.destroy?.();
      }
    },
    [],
  );

  // ✅ CLEANUP on unmount
  // useEffect(() => () => {
  //   if (playerRef.current) {
  //     // Remove from global player list
  //     window.activeYoutubePlayers = window.activeYoutubePlayers.filter(
  //       (p) => p !== playerRef.current
  //     );

  //     // Destroy the player instance
  //     playerRef.current.destroy?.();
  //   }
  // }, []);

  // If user clicked, show real YouTube player
  if (isClicked) {
    if (playWithID) {
      return (
        <div className={className}>
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
            </div>
          )}
          <div
            id={`youtube-player-${currentVideoId}`}
            className="w-full h-full"
          />
        </div>
      );
    }

    return (
      <>
        {isLoading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
          </div>
        )}
        <div
          className="w-full h-full"
          dangerouslySetInnerHTML={{
            __html: getAutoplayIframeData(iframeData),
          }}
        />
      </>
    );
  }

  // If not clicked, show facade with thumbnail and play button
  return (
    <YoutubeThumbnail
      className={className}
      currentVideoId={currentVideoId}
      onFacadeClick={handleFacadeClick}
    />
  );
}

export default YoutubeIframe;
